import styled from '@emotion/styled';
import type { GroupBase, StylesConfig } from 'react-select-new';

import { Icon } from '@anyroad/icon';
import { brandNeutralColors, colors, fontSizes } from '@anyroad/theme';

export type LocationOption = {
  cityState: string;
  label: string;
  value: string;
};

export const Label = styled.label({
  display: 'block',
  margin: '0px 0px 10px',
  textAlign: 'left',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: fontSizes.large,
  color: colors.text.primary,
  lineHeight: '20px',
  fontWeight: '600',
  width: 'initial',
});

export const Subtext = styled.div({
  color: colors.text.secondary,
  fontSize: fontSizes.small,
  lineHeight: 1.334,
  marginTop: '8px',
});

export const SelectWrapper = styled.div({
  position: 'relative',
  width: '100%',
});

export const InputErrorWrapper = styled.div({
  width: '100%',
});

export const InputError = styled.div({
  background: colors.background.error,
  color: colors.white,
  fontSize: fontSizes.small,
  lineHeight: 1.25,
  padding: '4px 8px',
});

export const RightIcon = styled(Icon)({
  position: 'absolute',
  right: '10px',
  top: '14px',
  pointerEvents: 'none',
});

export const getSelectStyles = (): StylesConfig<
  LocationOption,
  false,
  GroupBase<LocationOption>
> => ({
    container: (base) => ({
      ...base,
      fontSize: fontSizes.large,
      padding: '0',
      width: '100%',
    }),
    control: (base) => ({
      ...base,
      background: 'transparent',
      boxShadow: 'none',
      border: `1px solid ${colors.borderAction}`,
      borderRadius: 0,
      height: '44px',
      '&:hover': {
        border: `1px solid ${colors.borderAction}`,
      },
      '&::after': {
        boxShadow: `0px 0px 10px 0px ${colors.borderAction}`,
        clipPath: 'inset(-100% 0px 0px)',
      },
    }),
    valueContainer: (base) => ({
      ...base,
      height: '44px',
      padding: '0 16px',
    }),
    input: (base) => ({
      ...base,
      color: colors.text.primary,
      fontSize: fontSizes.large,
      margin: 0,
      padding: 0,
    }),
    placeholder: (base) => ({
      ...base,
      color: colors.text.placeholder,
      fontSize: fontSizes.large,
      margin: 0,
    }),
    singleValue: (base) => ({
      ...base,
      color: colors.text.primary,
      fontSize: fontSizes.large,
      margin: 0,
    }),
    indicatorsContainer: (base) => ({ ...base, padding: 0 }),
    menu: (base) => ({
      ...base,
      background: colors.white,
      border: `1px solid ${colors.borderAction}`,
      borderRadius: 0,
      borderTop: 'none',
      boxShadow: 'none',
      boxSizing: 'border-box',
      margin: 0,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    menuList: (base) => ({
      ...base,
      maxHeight: '367px',
      overflowY: 'auto',
      scrollbarColor: `${colors.border} ${colors.white}`,
      scrollbarWidth: 'thin',
      padding: '0',
    }),
    option: (base, { isSelected, isDisabled, isFocused }) => {
      const bg = isDisabled
        ? colors.white
        : isFocused
          ? brandNeutralColors.background.light
          : isSelected
            ? brandNeutralColors.background.light
            : colors.white;
      return {
        ...base,
        background: bg,
        boxShadow: `inset 0 1px 0 ${colors.background.light}`,
        color: isDisabled ? colors.text.placeholder : colors.text.primary,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        fontSize: fontSizes.large,
        fontWeight: '400',
        paddingLeft: '16px',
        alignItems: 'center',
        display: 'flex',
        flexWrap: 'nowrap',
        height: '44px',
        '&:active': {
          background: isDisabled ? colors.white : colors.background.active,
          boxShadow: 'none',
        },
        '&:hover': {
          background: isDisabled
            ? colors.white
            : brandNeutralColors.background.light,
          boxShadow: 'none',
        },
      };
    },
  });
